import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { JsonPlaceholderReplacer } from 'json-placeholder-replacer';
import { parseJson } from 'src/utilities/ApiHelper';
import {
  SchemaAttributes,
  SchemaTranslator,
} from 'src/integration/ddm/DDMSchemaTranslationProvider';
import { CaseCreationStaticSchema } from 'src/components/Forms/CreateCase/CreateCaseSchema';
import { ITranslationContext } from 'src/state/providers/TranslationProvider';

export class PaymentServicesComplaintSchemaTranslator
  implements SchemaTranslator
{
  private coreSchema: RJSFSchema;
  private uiSchema: UiSchema;
  private readonly caseSummarySchema: RJSFSchema;
  private createCaseStaticSchema: CaseCreationStaticSchema;
  private bundles: ITranslationContext;

  constructor(schemaAttributes: SchemaAttributes) {
    this.coreSchema = schemaAttributes.coreSchema;
    this.uiSchema = schemaAttributes.uiSchema;
    this.caseSummarySchema = schemaAttributes.caseSummarySchema;
    this.createCaseStaticSchema = schemaAttributes.createCaseStaticSchema;
    this.bundles = schemaAttributes.bundles;
  }

  translateCoreSchema(): RJSFSchema {
    return this.getPlaceholderReplacedCoreSchema(
      this.coreSchema,
      this.bundles.paymentServicesComplaintBundle,
    );
  }
  translateUiSchema(): UiSchema {
    return this.getPlaceholderReplacedUiSchema(
      this.uiSchema,
      this.bundles.paymentServicesComplaintBundle,
    );
  }
  translateCreateCaseStaticSchema(): CaseCreationStaticSchema {
    return this.getPlaceholderReplacedCreateCaseStaticSchema(
      this.createCaseStaticSchema,
      this.bundles.paymentServicesComplaintBundle,
    );
  }

  translateCaseSummarySchema(): RJSFSchema {
    return this.caseSummarySchema;
  }

  private getPlaceholderReplacedCoreSchema(
    schema: RJSFSchema,
    bundle: any,
  ): RJSFSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();
    placeHolderReplacer.addVariableMap({
      reporting_section_header: bundle.getMessage('reporting_section_header'),
      reporting_section_grounds_of_complaint_field_name: bundle.getMessage(
        'reporting_section_grounds_of_complaint_field_name',
      ),
      reporting_section_grounds_of_complaint_field_description:
        bundle.getMessage(
          'reporting_section_grounds_of_complaint_field_description',
        ),
      grounds_of_complaint_option_i_am_having_issues_with_opening_the_account:
        bundle.getMessage(
          'grounds_of_complaint_option_i_am_having_issues_with_opening_the_account',
        ),
      grounds_of_complaint_option_my_account_has_been_closed: bundle.getMessage(
        'grounds_of_complaint_option_my_account_has_been_closed',
      ),
      grounds_of_complaint_option_i_cant_use_my_account: bundle.getMessage(
        'grounds_of_complaint_option_i_cant_use_my_account',
      ),
      grounds_of_complaint_option_i_an_having_issues_with_my_funds_or_disbursement:
        bundle.getMessage(
          'grounds_of_complaint_option_i_an_having_issues_with_my_funds_or_disbursement',
        ),
      grounds_of_complaint_option_ive_been_charged_a_payment_or_fee_i_dont_recognise:
        bundle.getMessage(
          'grounds_of_complaint_option_ive_been_charged_a_payment_or_fee_i_dont_recognise',
        ),
      reporting_section_complaint_related_to_field_name: bundle.getMessage(
        'reporting_section_complaint_related_to_field_name',
      ),
      reporting_section_complaint_related_to_field_description:
        bundle.getMessage(
          'reporting_section_complaint_related_to_field_description',
        ),
      complaint_related_to_option_selling_on_amazon: bundle.getMessage(
        'complaint_related_to_option_selling_on_amazon',
      ),
      complaint_related_to_option_amazon_pay: bundle.getMessage(
        'complaint_related_to_option_amazon_pay',
      ),
      reporting_section_issue_occured_field_name: bundle.getMessage(
        'reporting_section_issue_occured_field_name',
      ),
      reporting_section_issue_occured_field_description: bundle.getMessage(
        'reporting_section_issue_occured_field_description',
      ),
      issue_occured_option_united_kingdom: bundle.getMessage(
        'issue_occured_option_united_kingdom',
      ),
      issue_occured_option_sweden: bundle.getMessage(
        'issue_occured_option_sweden',
      ),
      issue_occured_option_poland: bundle.getMessage(
        'issue_occured_option_poland',
      ),
      issue_occured_option_netherlands: bundle.getMessage(
        'issue_occured_option_netherlands',
      ),
      issue_occured_option_italy: bundle.getMessage(
        'issue_occured_option_italy',
      ),
      issue_occured_option_france: bundle.getMessage(
        'issue_occured_option_france',
      ),
      issue_occured_option_spain: bundle.getMessage(
        'issue_occured_option_spain',
      ),
      issue_occured_option_germany: bundle.getMessage(
        'issue_occured_option_germany',
      ),
      issue_occured_option_belgium: bundle.getMessage(
        'issue_occured_option_belgium',
      ),
      issue_occured_option_austria: bundle.getMessage(
        'issue_occured_option_austria',
      ),
      issue_occured_option_cyprus: bundle.getMessage(
        'issue_occured_option_cyprus',
      ),
      issue_occured_option_denmark: bundle.getMessage(
        'issue_occured_option_denmark',
      ),
      issue_occured_option_hungary: bundle.getMessage(
        'issue_occured_option_hungary',
      ),
      issue_occured_option_ireland: bundle.getMessage(
        'issue_occured_option_ireland',
      ),
      issue_occured_option_luxembourg: bundle.getMessage(
        'issue_occured_option_luxembourg',
      ),
      issue_occured_option_portugal: bundle.getMessage(
        'issue_occured_option_portugal',
      ),
      issue_occured_option_switzerland: bundle.getMessage(
        'issue_occured_option_switzerland',
      ),
      reporting_section_issue_description_field_name: bundle.getMessage(
        'reporting_section_issue_description_field_name',
      ),
      reporting_section_issue_description_field_description: bundle.getMessage(
        'reporting_section_issue_description_field_description',
      ),
      reporting_section_declaration_field_text: bundle.getMessage(
        'reporting_section_declaration_field_text',
      ),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedUiSchema(
    schema: UiSchema,
    bundle: any,
  ): UiSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      mandatory_field_error_message: bundle.getMessage(
        'mandatory_field_error_message',
      ),
      accept_tnc_field_error_message: bundle.getMessage(
        'accept_tnc_field_error_message',
      ),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedCreateCaseStaticSchema(
    schema: CaseCreationStaticSchema,
    bundle: any,
  ): CaseCreationStaticSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      referral_information_section_header: bundle.getMessage(
        'referral_information_section_header',
      ),
      referral_information_full_name_field_name: bundle.getMessage(
        'referral_information_full_name_field_name',
      ),
      referral_information_email_field_name: bundle.getMessage(
        'referral_information_email_field_name',
      ),
      referral_information_customer_id_field_name: bundle.getMessage(
        'referral_information_customer_id_field_name',
      ),
    });

    return placeHolderReplacer.replace(
      parseJson(JSON.stringify(schema)),
    ) as CaseCreationStaticSchema;
  }
}
